import React, { useEffect, useState } from 'react';
import { Loader2, ChevronRight, ChevronLeft, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { StageBadge } from './CRMCompanies';
import CRMCompanyDetail from './CRMCompanyDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMPipeline = ({ embedded = false }) => {
  const [board, setBoard] = useState({});
  const [stages, setStages] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [config, setConfig] = useState({ pipeline_stages: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetchWithAuth(`${API_URL}/api/crm/pipeline`),
      fetchWithAuth(`${API_URL}/api/crm/config`),
    ]).then(([p, c]) => { setBoard(p.board); setStages(p.stages); setStageCounts(p.stage_counts || {}); setConfig(c); }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const moveStage = async (company, direction) => {
    const idx = stages.indexOf(company.pipeline_stage);
    const targetStage = direction === 'lost' ? 'Lost / Closed' : stages[idx + direction];
    if (!targetStage) return;
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies/${company.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pipeline_stage: targetStage }) });
      toast.success(`${company.name} → ${targetStage}`);
      load();
    } catch (e) {
      toast.error('Could not move company');
    }
  };

  if (loading) {
    return (
      <IntelligenceLayout title="Pipeline" embedded={embedded}>
        <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      </IntelligenceLayout>
    );
  }

  return (
    <IntelligenceLayout title="Pipeline" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-pipeline-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Sales / Client Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">Prospect → Outreach → Response → Discovery → Agreement Sent → Agreement Signed → Active Client → Job Order → Placement / Won</p>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {stages.map(stage => {
            const cards = board[stage] || [];
            const total = stageCounts[stage] ?? cards.length;
            return (
              <div key={stage} className="flex-shrink-0 w-64" data-testid={`pipeline-column-${stage.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <StageBadge stage={stage} />
                  <span className="text-xs font-semibold text-gray-400" data-testid={`pipeline-column-count-${stage.replace(/\s+/g, '-').toLowerCase()}`}>{total}</span>
                </div>
                <div className="space-y-2 min-h-[80px]">
                  {cards.map(c => {
                    const idx = stages.indexOf(stage);
                    return (
                      <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm hover:border-[#D4AF37]/40 transition-colors" data-testid={`pipeline-card-${c.id}`}>
                        <button onClick={() => setSelectedId(c.id)} className="text-sm font-medium text-[#0A1F44] text-left hover:underline block truncate w-full" data-testid={`pipeline-card-open-${c.id}`}>
                          {c.name}
                        </button>
                        <p className="text-[11px] text-gray-400 truncate">{c.industry || c.location || c.lead_source || ''}</p>
                        {stage !== 'Lost / Closed' && (
                          <div className="flex items-center justify-between mt-1.5">
                            <button disabled={idx === 0} onClick={() => moveStage(c, -1)} data-testid={`move-back-${c.id}`} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5 text-gray-500" /></button>
                            <button onClick={() => moveStage(c, 'lost')} data-testid={`mark-lost-${c.id}`} className="p-1 rounded hover:bg-red-50" title="Mark Lost / Closed"><XCircle className="h-3.5 w-3.5 text-red-400" /></button>
                            <button disabled={idx >= stages.length - 2} onClick={() => moveStage(c, 1)} data-testid={`move-forward-${c.id}`} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5 text-gray-500" /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {total > cards.length && (
                    <p className="text-xs text-gray-400 text-center py-1">+{total - cards.length} more in Companies</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedId && <CRMCompanyDetail companyId={selectedId} config={config} onClose={() => setSelectedId(null)} onUpdated={load} />}
    </IntelligenceLayout>
  );
};

export default CRMPipeline;
